import {
  useIsDisabled,
  useDisabledColor,
  useAutoEndWarnings as useAutoEndWarnings,
  useLogoutWarnings,
  usePauseWarnings,
} from './utils';
import {
  DefaultAlertDialog,
  DefaultScreenTileBox,
  DurationViewer,
  LabeledText,
} from '~/common/components/defaultUIComponents';
import {
  Collapse,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
  PlusSquareIcon,
} from '@chakra-ui/icons';
import WorkingInfoApi from '~/common/api/zeiterfassungServerApi/WorkingInfoApi';
import { TimestampDate } from '~/common/entities/Employees/EmployeeTypes';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { CreateTimestampModal } from './CreateTimestampModal';
import { formatName } from '~/common/utils/employeeUtils';
import useTranslate from '~/common/utils/translate';
import WarningButtons from './WarningButtons';
import { TimestampBox } from './TimestampBox';
import * as dateFns from 'date-fns';
import { format } from 'date-fns';
import React from 'react';
import _ from 'lodash';

type Props = {
  employee: Employee;
  date: TimestampDate;
};

export const DayBox: React.FC<Props> = ({ employee, date }) => {
  const t = useTranslate();
  const { isOpen, onToggle } = useDisclosure();
  const { isOpen: isCreateOpen, onToggle: onCreateOpenToggle } =
    useDisclosure();
  const { isOpen: isDeleteOpen, onToggle: onDeleteOpenToggle } =
    useDisclosure();
  const disabled = useIsDisabled(date);
  const color = useDisabledColor(disabled);
  const autoEndWarnings = useAutoEndWarnings(date, employee);
  const logoutWarnings = useLogoutWarnings(date, employee);
  const pauseWarnings = usePauseWarnings(date, employee);

  const { mutate: deleteDate } = WorkingInfoApi.hooks.useDeleteDate();

  return (
    <>
      <DefaultScreenTileBox
        pl={1}
        mt={2}
        borderLeft="1px solid black"
        mx={!isOpen ? 0 : -2}
        shadow={!isOpen ? undefined : 'lg'}
        position="relative"
      >
        <Flex>
          <Flex mt={-2}>
            <IconButton
              icon={
                isOpen ? <ChevronUpIcon mt={-2} /> : <ChevronDownIcon mt={-2} />
              }
              onClick={onToggle}
              aria-label={t('expandTile') as string}
              isRound
              backgroundColor="transparent"
              color="black"
              _hover={{ color: 'black' }}
              _focus={{ boxShadow: 'none' }}
              fontSize="2xl"
              display="inline"
            />
            {isOpen && (
              <Tooltip
                label={
                  disabled
                    ? t('working.history.bone.edit.disabled')
                    : t('working.history.bone.add')
                }
              >
                <IconButton
                  icon={<PlusSquareIcon w={5} h={5} />}
                  onClick={() => !disabled && onCreateOpenToggle()}
                  aria-label={t('expandTile') as string}
                  isRound
                  backgroundColor="transparent"
                  _hover={{ color }}
                  _focus={{ boxShadow: 'none' }}
                  color={color}
                />
              </Tooltip>
            )}
          </Flex>
          <Grid
            templateColumns="repeat(9, 1fr)"
            w="100%"
            gap={1}
            position="relative"
          >
            <GridItem>
              <LabeledText label="working.history.bone.date" mt={0}>
                {format(date.date as unknown as number, 'dd.MM.yyy')}
              </LabeledText>
            </GridItem>
            <GridItem colSpan={3}>
              <DurationViewer
                label="working.history.bone.working_time"
                durationInSeconds={date.working_time}
              />
            </GridItem>
            <GridItem colSpan={1}>
              <DurationViewer
                label="working.history.bone.pause_time"
                durationInSeconds={date.pause_time}
              />
            </GridItem>
            <WarningButtons
              autoEndWarnings={autoEndWarnings}
              logoutWarnings={logoutWarnings}
              pauseWarnings={pauseWarnings}
              date={date.date as unknown as number}
              isDetailsOpen={isOpen}
              employee={employee}
            />
            <GridItem>
              <Tooltip
                label={
                  disabled
                    ? t('working.history.bone.edit.disabled')
                    : t('deleteDate.header')
                }
              >
                <IconButton
                  icon={<DeleteIcon w={5} h={5} />}
                  onClick={() => !disabled && onDeleteOpenToggle()}
                  aria-label={t('expandTile') as string}
                  isRound
                  backgroundColor="transparent"
                  _hover={{ color }}
                  _focus={{ boxShadow: 'none' }}
                  color={color}
                />
              </Tooltip>
            </GridItem>
            <GridItem
              colSpan={9}
              cursor="text"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Collapse in={isOpen}>
                {_.orderBy(date.timestamps, 'start', 'asc')?.map((ts) => (
                  <TimestampBox
                    key={ts.id}
                    ts={ts}
                    date={date}
                    employee={employee}
                  />
                ))}
              </Collapse>
            </GridItem>
          </Grid>
        </Flex>
      </DefaultScreenTileBox>
      <CreateTimestampModal
        employee={employee}
        date={date}
        isOpen={isCreateOpen}
        onToggle={onCreateOpenToggle}
      />
      <DefaultAlertDialog
        dialogHeaderText="deleteDate.header"
        dialogBodyText="deleteDate.body"
        bodyParams={[
          dateFns.format(date.date as unknown as number, 'dd.MM.yyyy'),
          formatName(employee),
        ]}
        isOpen={isDeleteOpen}
        onConfirmedAction={() => void deleteDate({ employee, date })}
        onCancel={onDeleteOpenToggle}
      />
    </>
  );
};
